import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  Lock,
  ArrowRight,
  ArrowLeft,
  Mail,
  KeyRound,
  CheckCircle2,
  RefreshCw,
  Sparkles,
  Shield,
  Layers,
  Server
} from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import methodModel from "../../methods/methods";
import FormControl from "../../components/common/FormControl";

const Forgotpassword = () => {
  const history = useNavigate();
  const WebDetails = useSelector((state) => state.logo);

  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({ email: "" });
  const [loading, setLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const [sentId, setSentId] = useState("");

  // Redirect if already authenticated
  useEffect(() => {
    if (localStorage.getItem("token")) {
      // keep on page or allow reset
    }
  }, []);

  // Cooldown countdown timer for Resend OTP
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Real-time Email Validation Handler matching Login page
  const handleEmailChange = (val) => {
    setEmail(val);
    const trimmed = String(val || "").trim();
    if (!trimmed) {
      setErrors((prev) => ({ ...prev, email: "Please enter your email address." }));
    } else {
      const check = methodModel.validateEmail(trimmed);
      setErrors((prev) => ({ ...prev, email: check.success ? "" : check.message }));
    }
  };

  // Submit Handler: calls user/admin/forgot/password
  const handleSubmit = (e) => {
    e.preventDefault();
    const emailTrimmed = (email || "").trim();

    // Validate email with exact same methodModel validator as Login page
    const emailValidation = methodModel.validateEmail(emailTrimmed);
    if (!emailValidation.success) {
      setErrors({ email: emailValidation.message });
      return;
    }

    setErrors({ email: "" });
    setLoading(true);
    loader(true);

    ApiClient.post(
      "user/admin/forgot/password",
      { email: emailTrimmed },
      "",
      true // hide default popup to handle inline with high aesthetics
    )
      .then((res) => {
        loader(false);
        setLoading(false);
        if (res?.success) {
          setCodeSent(true);
          setSentId(res?.id || "");
          setResendCooldown(30);
          toast.success(res?.message || "Password reset link sent to your email.");
        } else {
          const errMsg =
            res?.error?.message ||
            res?.message ||
            "Unable to find administrator account with this email.";
          setErrors({ email: errMsg });
          toast.error(errMsg);
        }
      })
      .catch((err) => {
        loader(false);
        setLoading(false);
        const errMsg =
          err?.response?.data?.error?.message ||
          err?.message ||
          "Failed to request password reset. Please try again.";
        setErrors({ email: errMsg });
        toast.error(errMsg);
      });
  };

  // Resend Handler: calls user/admin/resend-forgot-password-otp
  const handleResendOtp = () => {
    const emailTrimmed = (email || "").trim();
    if (!emailTrimmed) {
      setErrors({ email: "Please enter your email address." });
      return;
    }

    setResending(true);
    loader(true);

    ApiClient.post(
      "user/admin/resend-forgot-password-otp",
      { email: emailTrimmed },
      "",
      true
    )
      .then((res) => {
        loader(false);
        setResending(false);
        if (res?.success) {
          setResendCooldown(30);
          if (res?.id) setSentId(res.id);
          toast.success(res?.message || "A fresh 6-digit code has been emailed to you.");
        } else {
          const errMsg = res?.error?.message || res?.message || "Failed to resend code.";
          toast.error(errMsg);
        }
      })
      .catch((err) => {
        loader(false);
        setResending(false);
        const errMsg =
          err?.response?.data?.error?.message || err?.message || "Failed to resend verification code.";
        toast.error(errMsg);
      });
  };

  const logoSrc = WebDetails?.logo || "/assets/img/logo.png";

  return (
    <>
      <div className="h-screen w-full flex flex-col lg:flex-row bg-white lg:bg-[#081325] overflow-hidden antialiased selection:bg-[#115B9D] selection:text-white">

        {/* ======================================================== */}
        {/* LEFT BRAND & PLATFORM SHOWCASE PANEL (Visible on lg+) */}
        {/* ======================================================== */}
        <div className="hidden lg:flex lg:w-1/2 xl:w-[52%] h-full relative flex-col justify-between p-6 xl:p-10 2xl:p-12 overflow-hidden bg-gradient-to-br from-[#061224] via-[#0B2545] to-[#115B9D] text-white select-none">

          {/* Ambient Glow Orbs */}
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-sky-500/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute top-1/2 -right-24 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 left-1/3 w-80 h-80 bg-[#115B9D]/30 rounded-full blur-3xl pointer-events-none" />

          {/* Subtle Grid Pattern Overlay */}
          <div
            className="absolute inset-0 opacity-[0.07] pointer-events-none"
            style={{
              backgroundImage: "radial-gradient(#ffffff 1px, transparent 1px)",
              backgroundSize: "28px 28px"
            }}
          />

          {/* Top Branding Bar */}
          <div className="relative z-10 flex items-center justify-between">
            <div className="inline-flex items-center gap-2.5 bg-white/10 backdrop-blur-md px-3.5 py-2 rounded-2xl border border-white/15 shadow-xl">
              <img
                src={logoSrc}
                alt="Charles S. Smith & Associates"
                className="h-9 w-auto object-contain bg-white rounded-lg p-1 shadow-sm"
              />
              <div className="leading-tight">
                <span className="block text-[10px] uppercase font-bold tracking-widest text-sky-300">
                  E-Commerce Suite
                </span>
                <span className="block text-xs font-semibold text-white tracking-tight">
                  Charles S. Smith & Associates
                </span>
              </div>
            </div>

            {/* Live Status Pill */}
            <div className="hidden xl:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-xs font-medium backdrop-blur-sm">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
              </span>
              Systems Operational
            </div>
          </div>

          {/* Center Showcase Content */}
          <div className="relative z-10 my-auto py-4 xl:py-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-400/15 border border-sky-300/30 text-sky-200 text-xs font-semibold uppercase tracking-wider mb-3">
              <KeyRound className="w-3.5 h-3.5 text-sky-300" />
              Password Recovery
            </div>

            <h1 className="text-2xl xl:text-3xl 2xl:text-4xl font-extrabold text-white tracking-tight leading-tight">
              Enterprise Access Restoration For{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-300 via-sky-200 to-white">
                Platform Administrators
              </span>
            </h1>

            <p className="mt-2.5 text-xs xl:text-sm text-sky-100 leading-relaxed max-w-lg font-normal">
              Enter your registered administrator email to receive a secure password reset link and recover your account credentials.
            </p>

            {/* Feature Cards Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-2.5 mt-5">
              <div className="bg-white/10 backdrop-blur-md p-3 rounded-xl border border-white/15 hover:bg-white/15 transition-all">
                <div className="w-7 h-7 rounded-lg bg-sky-400/20 flex items-center justify-center text-sky-300 mb-2">
                  <Mail className="w-3.5 h-3.5" />
                </div>
                <h2 className="text-xs font-bold text-white tracking-wide">Reset Link</h2>
                <p className="text-[11px] text-sky-100/90 mt-0.5 leading-tight">
                  Dispatched instantly to your administrator inbox.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-md p-3 rounded-xl border border-white/15 hover:bg-white/15 transition-all">
                <div className="w-7 h-7 rounded-lg bg-indigo-400/20 flex items-center justify-center text-indigo-300 mb-2">
                  <Shield className="w-3.5 h-3.5" />
                </div>
                <h2 className="text-xs font-bold text-white tracking-wide">SSL Protected</h2>
                <p className="text-[11px] text-sky-100/90 mt-0.5 leading-tight">
                  High-security token validation preventing breach.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-md p-3 rounded-xl border border-white/15 hover:bg-white/15 transition-all">
                <div className="w-7 h-7 rounded-lg bg-emerald-400/20 flex items-center justify-center text-emerald-300 mb-2">
                  <RefreshCw className="w-3.5 h-3.5" />
                </div>
                <h2 className="text-xs font-bold text-white tracking-wide">Direct Reset</h2>
                <p className="text-[11px] text-sky-100/90 mt-0.5 leading-tight">
                  Quick one-click access directly from your email.
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Trust & Heritage Footer */}
          <div className="relative z-10 pt-4 border-t border-white/10 flex items-center justify-between text-xs text-sky-100">
            <span className="font-semibold text-white">
              Serving the Americas Since 1976
            </span>
            <span className="flex items-center gap-1.5 text-sky-200 font-medium">
              <Lock className="w-3.5 h-3.5 text-sky-300" /> 256-Bit SSL Encrypted
            </span>
          </div>
        </div>

        {/* ======================================================== */}
        {/* RIGHT FORM PANEL (Matches Login Page Structure) */}
        {/* ======================================================== */}
        <div className="w-full lg:w-1/2 xl:w-[48%] h-full flex items-center justify-center px-6 py-6 sm:px-10 sm:py-8 lg:px-12 xl:px-16 bg-white overflow-y-auto overflow-x-hidden">
          <div className="w-full max-w-[400px] my-auto py-2">

            {/* Header with Logo */}
            <div className="text-center mb-5 sm:mb-6">
              <img
                src={logoSrc}
                alt="Platform Logo"
                className="h-10 w-auto object-contain mx-auto mb-2"
              />
              <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
                {codeSent ? "Check Your Email" : "Forgot Password?"}
              </h2>
              <p className="text-xs text-slate-700 mt-1 leading-relaxed font-medium">
                {codeSent
                  ? `A password reset link has been dispatched to ${email}.`
                  : "Enter your administrator email to receive a password reset link."}
              </p>
            </div>

            {/* Code Sent Success State Card */}
            {codeSent ? (
              <div className="space-y-4">
                {/* Eye-catching Success Card matching App Theme */}
                <div className="p-5 sm:p-6 rounded-2xl bg-gradient-to-b from-sky-50/70 via-white to-blue-50/25 border border-sky-100 shadow-sm text-center relative overflow-hidden">
                  
                  {/* Subtle Background Accent Glows */}
                  <div className="absolute -top-12 -right-12 w-36 h-36 bg-sky-400/10 rounded-full blur-2xl pointer-events-none" />
                  <div className="absolute -bottom-12 -left-12 w-36 h-36 bg-[#115B9D]/10 rounded-full blur-2xl pointer-events-none" />

                  {/* Mail Icon with Glowing Verification Badge */}
                  <div className="relative mx-auto w-14 h-14 mb-3.5 flex items-center justify-center">
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-[#115B9D]/20 to-sky-400/25 blur-sm" />
                    <div className="relative w-14 h-14 rounded-2xl bg-white border border-[#115B9D]/20 shadow-md shadow-[#115B9D]/10 flex items-center justify-center text-[#115B9D]">
                      <Mail className="w-7 h-7 text-[#115B9D]" />
                      <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center ring-2 ring-white shadow-xs">
                        <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                      </span>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="font-bold text-base sm:text-lg text-slate-900 tracking-tight">
                    Reset Link Dispatched!
                  </h3>

                  {/* Informative message telling user to check email */}
                  <p className="text-xs sm:text-[13px] text-slate-600 mt-1.5 leading-relaxed font-normal">
                    A forgot password link has been sent to the following email address. Please go and check your inbox so that you can change your password:
                  </p>

                  {/* Highlighted Email Pill */}
                  <div className="mt-3 mb-3 px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200/90 flex items-center justify-center gap-2 shadow-xs">
                    <Mail className="w-4 h-4 text-[#115B9D] shrink-0" />
                    <span className="font-bold text-xs sm:text-sm text-slate-900 break-all select-all">
                      {email}
                    </span>
                  </div>

                  {/* Next Step Guide */}
                  <div className="pt-3 border-t border-slate-100 text-left space-y-2">
                    <div className="flex items-start gap-2.5 text-xs text-slate-700 font-medium">
                      <span className="flex h-5 w-5 rounded-full bg-[#115B9D] text-white items-center justify-center text-[10px] font-bold shrink-0 mt-0.5 shadow-xs">
                        1
                      </span>
                      <span>Open the email received from <strong>Charles S. Smith & Associates</strong>.</span>
                    </div>
                    <div className="flex items-start gap-2.5 text-xs text-slate-700 font-medium">
                      <span className="flex h-5 w-5 rounded-full bg-[#115B9D] text-white items-center justify-center text-[10px] font-bold shrink-0 mt-0.5 shadow-xs">
                        2
                      </span>
                      <span>Click on the <strong>password reset link</strong> provided in the email.</span>
                    </div>
                    <div className="flex items-start gap-2.5 text-xs text-slate-700 font-medium">
                      <span className="flex h-5 w-5 rounded-full bg-[#115B9D] text-white items-center justify-center text-[10px] font-bold shrink-0 mt-0.5 shadow-xs">
                        3
                      </span>
                      <span>Enter and confirm your new secure password to log in.</span>
                    </div>
                  </div>

                  {/* Spam Folder Advice */}
                  <p className="text-[11px] text-slate-500 mt-3 pt-2.5 border-t border-slate-100 font-medium">
                    Can't find the email? Please be sure to check your <strong>Spam</strong> or <strong>Junk</strong> folder.
                  </p>
                </div>

                {/* ========================================================================= */}
                {/* BUTTONS COMMENTED OUT AS REQUESTED:                                       */}
                {/* A password reset link has been dispatched to the user's email. Users      */}
                {/* will click the link directly in their inbox to reset their password,     */}
                {/* making manual OTP code submission buttons obsolete.                       */}
                {/* ========================================================================= */}
                {/*
                <button
                  type="button"
                  onClick={() =>
                    history(
                      `/reset-password?userId=${encodeURIComponent(sentId)}&email=${encodeURIComponent(email)}`
                    )
                  }
                  className="w-full h-10 sm:h-11 rounded-xl bg-gradient-to-r from-[#115B9D] via-[#0E528F] to-[#0A3E6E] hover:from-[#0E528F] hover:to-[#08335C] text-white font-bold text-sm sm:text-base shadow-lg shadow-[#115B9D]/25 hover:shadow-xl hover:shadow-[#115B9D]/35 active:scale-[0.99] transition-all duration-200 flex items-center justify-center gap-2 group cursor-pointer"
                >
                  <span>Enter Code & Reset Password</span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>

                <div className="text-center pt-1">
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={resendCooldown > 0 || resending}
                    className="inline-flex items-center gap-2 text-xs font-bold text-[#115B9D] hover:text-[#0b4377] hover:underline transition-colors disabled:opacity-50 disabled:no-underline cursor-pointer"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${resending ? "animate-spin" : ""}`} />
                    <span>
                      {resendCooldown > 0
                        ? `Resend Code in ${resendCooldown}s`
                        : resending
                        ? "Resending Code..."
                        : "Didn't receive the code? Resend Code"}
                    </span>
                  </button>
                </div>
                */}

                {/* Change Email Action */}
                <div className="text-center pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setCodeSent(false);
                      setErrors({ email: "" });
                    }}
                    className="text-xs font-semibold text-slate-600 hover:text-[#115B9D] transition-colors cursor-pointer"
                  >
                    Entered the wrong email? <span className="underline font-bold text-[#115B9D]">Change Email</span>
                  </button>
                </div>
              </div>
            ) : (
              /* Request Form */
              <form onSubmit={handleSubmit} noValidate className="space-y-4">

                {/* Email Field matching Login page */}
                <FormControl
                  type="text"
                  name="email"
                  id="recoveryEmail"
                  label="Email Address"
                  required={true}
                  placeholder="superadmin@yopmail.com"
                  value={email}
                  error={errors.email}
                  onChange={handleEmailChange}
                  autoComplete="email"
                  divClass="space-y-1"
                  labelClass="!text-xs !font-bold !uppercase !tracking-wider !text-slate-900 !mb-1"
                  className="!h-10 sm:!h-11 !rounded-xl text-slate-900 text-sm shadow-none transition-none"
                />

                {/* Submit Action Button matching Login Page */}
                <div className="pt-1.5">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full h-10 sm:h-11 rounded-xl bg-gradient-to-r from-[#115B9D] via-[#0E528F] to-[#0A3E6E] hover:from-[#0E528F] hover:to-[#08335C] text-white font-bold text-sm sm:text-base shadow-lg shadow-[#115B9D]/25 hover:shadow-xl hover:shadow-[#115B9D]/35 active:scale-[0.99] transition-all duration-200 flex items-center justify-center gap-2 group cursor-pointer disabled:opacity-75 disabled:cursor-not-allowed"
                  >
                    <span>{loading ? "Sending Reset Link..." : "Send Reset Link"}</span>
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </button>
                </div>

              </form>
            )}

            {/* Back to Sign In Link */}
            <div className="mt-5 pt-3.5 border-t border-slate-200 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-slate-700 hover:text-slate-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Remember your password? <strong className="text-[#115B9D] font-bold">Sign In</strong></span>
              </Link>
            </div>

            {/* Security & Support Footer */}
            <div className="mt-4 pt-3 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-1.5 text-[11px] text-slate-700">
              <span className="flex items-center gap-1.5 font-semibold text-slate-800">
                <Lock className="w-3.5 h-3.5 text-[#115B9D]" /> 256-Bit SSL Encrypted
              </span>
              <span className="font-semibold text-slate-800">
                Authorized Personnel Only
              </span>
            </div>

            {/* Copyright Note */}
            <p className="text-center text-[11px] text-slate-600 mt-2 font-medium">
              &copy; {new Date().getFullYear()} Charles S. Smith & Associates. All rights reserved.
            </p>

          </div>
        </div>

      </div>
      <ToastContainer position="top-right" />
    </>
  );
};

export default Forgotpassword;
